import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Search, Car, ChevronUp, ChevronDown, Globe, Upload, Star, X } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  disponibile: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-700",
  noleggiata: "bg-red-100 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-400 dark:border-red-700",
  manutenzione: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-700",
};
const statusLabels = {
  disponibile: "Disponibile",
  noleggiata: "Noleggiata",
  manutenzione: "Manutenzione",
};

const emptyVehicle = { brand: "", model: "", plate: "", year: new Date().getFullYear(), daily_rate: 0, fuel_type: "Benzina", color: "", km: 0, status: "disponibile", notes: "" };
const emptyPub = { show_on_site: false, site_categories: [], public_title: "", short_description: "", full_description: "", price_from: 0, seats: 5, doors: 5, transmission: "Manuale", ac: true, features: "" };

export default function FleetPage() {
  const { axiosAuth } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyVehicle });
  const [pub, setPub] = useState({ ...emptyPub });
  const [photos, setPhotos] = useState([]);
  const [mainPhoto, setMainPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [siteCategories, setSiteCategories] = useState([]);
  const [catDialog, setCatDialog] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);

  const fetchCategories = useCallback(async () => {
    try { const api = axiosAuth(); const res = await api.get("/website/categories"); setSiteCategories(res.data.filter((c) => c.active)); } catch {}
  }, [axiosAuth]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const reorderVehicle = async (vid, direction) => {
    try {
      const api = axiosAuth();
      await api.patch("/vehicles/reorder", { vehicle_id: vid, direction });
      fetchVehicles();
    } catch { toast.error("Errore riordino"); }
  };

  const fetchVehicles = useCallback(async () => {
    try {
      const api = axiosAuth();
      const res = await api.get("/vehicles");
      setVehicles(res.data);
    } catch (err) {
      toast.error("Errore nel caricamento veicoli");
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const api = axiosAuth();
      const pubPayload = { ...pub, price_from: parseFloat(pub.price_from) || 0, public_title: pub.public_title || `${form.brand} ${form.model}`.trim() };
      if (editing) {
        await api.put(`/vehicles/${editing.id}`, { ...form, ...pubPayload });
        toast.success("Veicolo aggiornato");
      } else {
        const res = await api.post("/vehicles", form);
        if (pub.show_on_site) await api.put(`/vehicles/${res.data.id}`, pubPayload);
        toast.success("Veicolo aggiunto");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyVehicle });
      setPub({ ...emptyPub });
      fetchVehicles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      const api = axiosAuth();
      await api.delete(`/vehicles/${deleteDialog.id}`);
      toast.success("Veicolo eliminato");
      setDeleteDialog(null);
      fetchVehicles();
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({ brand: v.brand, model: v.model, plate: v.plate, year: v.year, daily_rate: v.daily_rate, fuel_type: v.fuel_type, color: v.color || "", km: v.km || 0, status: v.status, notes: v.notes || "" });
    setPub({
      show_on_site: v.show_on_site || false,
      site_categories: v.site_categories || [],
      public_title: v.public_title || "",
      short_description: v.short_description || "",
      full_description: v.full_description || "",
      price_from: v.price_from || v.daily_rate || 0,
      seats: v.seats || 5, doors: v.doors || 5,
      transmission: v.transmission || "Manuale",
      ac: v.ac !== false, features: v.features || "",
    });
    setPhotos(v.photos || []);
    setMainPhoto(v.main_photo || "");
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyVehicle });
    setPub({ ...emptyPub });
    setPhotos([]);
    setMainPhoto("");
    setDialogOpen(true);
  };

  const toggleCategory = (cid) => {
    setPub((p) => ({ ...p, site_categories: p.site_categories.includes(cid) ? p.site_categories.filter((x) => x !== cid) : [...p.site_categories, cid] }));
  };

  const quickCreateCategory = async () => {
    try {
      const api = axiosAuth();
      const res = await api.post("/website/categories", { name: catDialog.name, description: catDialog.description });
      setSiteCategories((prev) => [...prev, res.data]);
      setPub((p) => ({ ...p, site_categories: [...p.site_categories, res.data.id] }));
      setCatDialog(null);
      toast.success("Categoria creata e selezionata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setUploading(true);
    try {
      const api = axiosAuth();
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/website/vehicles/${editing.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(res.data.photos);
      setMainPhoto(res.data.main_photo);
      toast.success("Foto caricata");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore caricamento foto"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const deletePhoto = async (url) => {
    try {
      const api = axiosAuth();
      const res = await api.delete(`/website/vehicles/${editing.id}/photos`, { params: { url } });
      setPhotos(res.data.photos);
      setMainPhoto(res.data.main_photo);
    } catch { toast.error("Errore eliminazione foto"); }
  };

  const setAsMain = async (url) => {
    try {
      const api = axiosAuth();
      await api.put(`/vehicles/${editing.id}`, { main_photo: url });
      setMainPhoto(url);
      toast.success("Foto principale impostata");
    } catch { toast.error("Errore"); }
  };

  const filtered = vehicles.filter((v) => {
    const q = search.toLowerCase();
    return `${v.brand} ${v.model} ${v.plate}`.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="fleet-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-slate-100">Gestione Flotta</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{vehicles.length} veicoli registrati</p>
        </div>
        <Button onClick={openNew} data-testid="add-vehicle-button" className="bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-sm hover:shadow-red-200/50 transition-all">
          <Plus className="w-4 h-4 mr-2" /> Aggiungi Veicolo
        </Button>
      </div>

      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
        <CardHeader className="pb-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per marca, modello, targa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 rounded-lg"
              data-testid="fleet-search-input"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50 dark:bg-slate-800/50">
                <TableHead className="font-medium text-slate-700 dark:text-slate-200">Veicolo</TableHead>
                <TableHead className="font-medium text-slate-700 dark:text-slate-200 hidden md:table-cell">Targa</TableHead>
                <TableHead className="font-medium text-slate-700 dark:text-slate-200 hidden lg:table-cell">Anno</TableHead>
                <TableHead className="font-medium text-slate-700 dark:text-slate-200">Tariffa/G</TableHead>
                <TableHead className="font-medium text-slate-700 dark:text-slate-200">Stato</TableHead>
                <TableHead className="font-medium text-slate-700 dark:text-slate-200 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  {search ? "Nessun veicolo trovato" : "Nessun veicolo. Aggiungi il primo!"}
                </TableCell></TableRow>
              ) : (
                filtered.map((v) => (
                  <TableRow key={v.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50" data-testid={`vehicle-row-${v.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center">
                          <Car className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white text-sm">{v.brand} {v.model}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 md:hidden font-mono">{v.plate}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="font-mono text-sm text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">{v.plate}</span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600 dark:text-slate-300 text-sm">{v.year}</TableCell>
                    <TableCell className="font-medium text-sm text-slate-900 dark:text-white">{v.daily_rate?.toFixed(2)} EUR</TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[v.status] || "bg-slate-100 text-slate-600 dark:text-slate-300"} border text-xs`}>
                        {statusLabels[v.status] || v.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <div className="flex flex-col mr-1">
                          <Button variant="ghost" size="icon" onClick={() => reorderVehicle(v.id, "up")} className="h-5 w-5 text-slate-400 hover:text-slate-700 dark:hover:text-white" data-testid={`move-up-${v.id}`}>
                            <ChevronUp className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => reorderVehicle(v.id, "down")} className="h-5 w-5 text-slate-400 hover:text-slate-700 dark:hover:text-white" data-testid={`move-down-${v.id}`}>
                            <ChevronDown className="w-3 h-3" />
                          </Button>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.id}`} className="h-8 w-8 text-slate-500 hover:text-slate-700 dark:text-slate-200">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteDialog(v)} data-testid={`delete-vehicle-${v.id}`} className="h-8 w-8 text-slate-500 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">{editing ? "Modifica Veicolo" : "Nuovo Veicolo"}</DialogTitle>
            <DialogDescription>
              {editing ? "Modifica i dati del veicolo" : "Inserisci i dati del nuovo veicolo"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Marca</Label>
              <Input data-testid="vehicle-brand-input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="es. Fiat" className="h-10 rounded-lg" />
            </div>
            <div className="space-y-2">
              <Label>Modello</Label>
              <Input data-testid="vehicle-model-input" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="es. 500" className="h-10 rounded-lg" />
            </div>
            <div className="space-y-2">
              <Label>Targa</Label>
              <Input data-testid="vehicle-plate-input" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} placeholder="es. AB123CD" className="h-10 rounded-lg font-mono" />
            </div>
            <div className="space-y-2">
              <Label>Anno</Label>
              <Input data-testid="vehicle-year-input" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) || 2024 })} className="h-10 rounded-lg" />
            </div>
            <div className="space-y-2">
              <Label>Tariffa Giornaliera (EUR)</Label>
              <Input data-testid="vehicle-rate-input" type="number" step="0.01" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: parseFloat(e.target.value) || 0 })} className="h-10 rounded-lg" />
            </div>
            <div className="space-y-2">
              <Label>Carburante</Label>
              <Select value={form.fuel_type} onValueChange={(val) => setForm({ ...form, fuel_type: val })} data-testid="vehicle-fuel-select">
                <SelectTrigger className="h-10 rounded-lg" data-testid="vehicle-fuel-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Benzina">Benzina</SelectItem>
                  <SelectItem value="Diesel">Diesel</SelectItem>
                  <SelectItem value="GPL">GPL</SelectItem>
                  <SelectItem value="Elettrica">Elettrica</SelectItem>
                  <SelectItem value="Ibrida">Ibrida</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Colore</Label>
              <Input data-testid="vehicle-color-input" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="es. Rosso" className="h-10 rounded-lg" />
            </div>
            <div className="space-y-2">
              <Label>Km</Label>
              <Input data-testid="vehicle-km-input" type="number" value={form.km} onChange={(e) => setForm({ ...form, km: parseInt(e.target.value) || 0 })} className="h-10 rounded-lg" />
            </div>
            {editing && (
              <div className="space-y-2 col-span-2">
                <Label>Stato</Label>
                <Select value={form.status} onValueChange={(val) => setForm({ ...form, status: val })}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid="vehicle-status-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disponibile">Disponibile</SelectItem>
                    <SelectItem value="noleggiata">Noleggiata</SelectItem>
                    <SelectItem value="manutenzione">Manutenzione</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* PUBBLICAZIONE SITO */}
          <div className="mt-2 border border-amber-200 dark:border-amber-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-amber-600" />
                <span className="font-outfit font-semibold text-sm text-slate-900 dark:text-white">Pubblicazione Sito</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Mostra sul sito</span>
                <Switch checked={pub.show_on_site} onCheckedChange={(v) => setPub({ ...pub, show_on_site: v })} data-testid="pub-show-on-site" />
              </div>
            </div>
            {pub.show_on_site && (
              <div className="p-4 space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Categorie sito</Label>
                    <button type="button" onClick={() => setCatDialog({ name: "", description: "" })} className="text-xs font-medium text-amber-600 hover:text-amber-700" data-testid="pub-create-category-btn">+ Crea nuova categoria</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {siteCategories.map((c) => (
                      <button key={c.id} type="button" onClick={() => toggleCategory(c.id)}
                        className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${pub.site_categories.includes(c.id) ? "bg-slate-900 text-white border-slate-900 dark:bg-amber-500 dark:text-slate-900 dark:border-amber-500" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-400"}`}
                        data-testid={`pub-category-${c.id}`}>
                        {c.name}
                      </button>
                    ))}
                    {siteCategories.length === 0 && <p className="text-xs text-slate-400">Nessuna categoria attiva. Creane una.</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label className="text-xs">Titolo pubblico</Label>
                    <Input value={pub.public_title} onChange={(e) => setPub({ ...pub, public_title: e.target.value })} placeholder={`${form.brand} ${form.model}`.trim() || "es. Fiat Panda"} className="h-9 rounded-lg" data-testid="pub-title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Prezzo "a partire da" (€/giorno)</Label>
                    <Input type="text" inputMode="decimal" value={pub.price_from} onChange={(e) => setPub({ ...pub, price_from: e.target.value.replace(",", ".") })} className="h-9 rounded-lg" data-testid="pub-price-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Cambio</Label>
                    <Select value={pub.transmission} onValueChange={(v) => setPub({ ...pub, transmission: v })}>
                      <SelectTrigger className="h-9 rounded-lg" data-testid="pub-transmission-trigger"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Manuale">Manuale</SelectItem>
                        <SelectItem value="Automatico">Automatico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Posti</Label>
                    <Input type="number" value={pub.seats} onChange={(e) => setPub({ ...pub, seats: parseInt(e.target.value) || 0 })} className="h-9 rounded-lg" data-testid="pub-seats-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Porte</Label>
                    <Input type="number" value={pub.doors} onChange={(e) => setPub({ ...pub, doors: parseInt(e.target.value) || 0 })} className="h-9 rounded-lg" data-testid="pub-doors-input" />
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <Switch checked={pub.ac} onCheckedChange={(v) => setPub({ ...pub, ac: v })} data-testid="pub-ac-switch" />
                    <Label className="text-xs">Climatizzatore</Label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Descrizione breve</Label>
                  <Input value={pub.short_description} onChange={(e) => setPub({ ...pub, short_description: e.target.value })} placeholder="Una frase che presenta il veicolo" className="h-9 rounded-lg" data-testid="pub-short-desc-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Descrizione completa</Label>
                  <Textarea value={pub.full_description} onChange={(e) => setPub({ ...pub, full_description: e.target.value })} className="min-h-[70px] rounded-lg" data-testid="pub-full-desc-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Optional / caratteristiche (separate da virgola)</Label>
                  <Input value={pub.features} onChange={(e) => setPub({ ...pub, features: e.target.value })} placeholder="es. Bluetooth, Sensori parcheggio, Cruise control" className="h-9 rounded-lg" data-testid="pub-features-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Foto sito {mainPhoto && <span className="text-amber-600">(★ = principale)</span>}</Label>
                  {editing ? (
                    <>
                      <div className="grid grid-cols-4 gap-2">
                        {photos.map((p) => (
                          <div key={p} className={`relative aspect-[4/3] rounded-lg overflow-hidden border-2 ${mainPhoto === p ? "border-amber-500" : "border-transparent"}`} data-testid={`pub-photo-${p.split("/").pop()}`}>
                            <img src={p} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                              <button type="button" onClick={() => setAsMain(p)} title="Imposta principale" className="p-1 bg-white/90 rounded text-amber-600"><Star className="w-3.5 h-3.5" /></button>
                              <button type="button" onClick={() => deletePhoto(p)} title="Elimina" className="p-1 bg-white/90 rounded text-red-600"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          </div>
                        ))}
                        <label className="aspect-[4/3] rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex flex-col items-center justify-center cursor-pointer hover:border-amber-400 transition-colors">
                          <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="pub-photo-upload" />
                          <Upload className={`w-4 h-4 ${uploading ? "text-amber-500 animate-pulse" : "text-slate-400"}`} />
                          <span className="text-[9px] text-slate-400 mt-1">{uploading ? "Carico..." : "Aggiungi"}</span>
                        </label>
                      </div>
                      <p className="text-[10px] text-slate-400">Le foto vengono compresse automaticamente per il web.</p>
                    </>
                  ) : (
                    <p className="text-xs text-slate-400 bg-slate-50 dark:bg-slate-800 rounded-lg p-3">Salva prima il veicolo, poi riaprilo per caricare le foto.</p>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Annulla</Button>
            <Button onClick={handleSave} disabled={submitting || !form.brand || !form.model || !form.plate} data-testid="vehicle-save-button" className="bg-red-600 hover:bg-red-700 text-white rounded-lg">
              {submitting ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick create category */}
      <Dialog open={!!catDialog} onOpenChange={() => setCatDialog(null)}>
        <DialogContent className="sm:max-w-sm rounded-xl" data-testid="quick-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">Crea Nuova Categoria</DialogTitle>
            <DialogDescription>Sarà subito selezionabile e comparirà nei filtri del sito.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome categoria</Label>
              <Input value={catDialog?.name || ""} onChange={(e) => setCatDialog({ ...catDialog, name: e.target.value })} placeholder="es. Elettriche" data-testid="quick-category-name" />
            </div>
            <div className="space-y-2">
              <Label>Descrizione (opzionale)</Label>
              <Input value={catDialog?.description || ""} onChange={(e) => setCatDialog({ ...catDialog, description: e.target.value })} data-testid="quick-category-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog(null)} className="rounded-lg">Annulla</Button>
            <Button onClick={quickCreateCategory} disabled={!catDialog?.name?.trim()} className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg" data-testid="quick-category-save">Crea Categoria</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="delete-vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">Elimina Veicolo</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare {deleteDialog?.brand} {deleteDialog?.model} ({deleteDialog?.plate})?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="rounded-lg">Annulla</Button>
            <Button onClick={handleDelete} data-testid="confirm-delete-vehicle-button" className="bg-red-600 hover:bg-red-700 text-white rounded-lg">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
